import React, { useMemo, useState } from 'react';
import '../styles/pedido-delivery-note.css';

/**
 * Extrae el medio de pago y la dirección de domicilio desde las notas del
 * pedido generado por el bot, con formato:
 *   "Catálogo web · Pago: <medio> · Entrega: Domicilio: <dirección>"
 *   "Catálogo web · Pago: <medio> · Entrega: Retiro en mostrador"
 */
export function parseDelivery(notas) {
  const text = String(notas || '');
  const out = { pago: null, domicilio: null, mostrador: false, coords: null, sucursal: null };

  const pago = text.match(/Pago:\s*([^·]+?)(?:\s*·|$)/i);
  if (pago) out.pago = pago[1].trim();

  // La dirección no contiene "·"; cortamos antes del segmento "· Geo:" si existe.
  const dom = text.match(/Entrega:\s*Domicilio:\s*([^·]+?)\s*(?:·|$)/i);
  if (dom && dom[1]) {
    out.domicilio = dom[1].trim();
  } else if (/Entrega:\s*Retiro\s+en\s+mostrador/i.test(text)) {
    out.mostrador = true;
  }

  const sucursal = text.match(/Sucursal:\s*([^·]+?)(?:\s*·|$)/i);
  if (sucursal) out.sucursal = sucursal[1].trim();

  const geo = text.match(/Geo:\s*(-?\d{1,3}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)/i);
  if (geo) {
    out.coords = `${geo[1]},${geo[2]}`;
  }
  return out;
}

export function stripDeliveryStructuredSegments(notas) {
  const text = String(notas || '').trim();
  if (!text) return '';

  return text
    .replace(/\bCat[aá]logo web\b\s*(?:·\s*)?/gi, '')
    .replace(/Pago:\s*([^·]+?)(?:\s*·|$)/gi, '')
    .replace(/Entrega:\s*Domicilio:\s*([^·]+?)(?:\s*·|$)/gi, '')
    .replace(/Entrega:\s*Retiro\s+en\s+mostrador(?:\s*·|$)/gi, '')
    .replace(/Sucursal:\s*([^·]+?)(?:\s*·|$)/gi, '')
    .replace(/Geo:\s*(-?\d{1,3}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)(?:\s*·|$)/gi, '')
    .replace(/\s*·\s*/g, ' · ')
    .replace(/^(\s*·\s*)+|(\s*·\s*)+$/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function buildMapSources({ coords = '', address = '', label = '', zoom = 16 }) {
  const coordsText = String(coords || '').trim();
  const addressText = String(address || '').trim();
  const labelText = String(label || '').trim();
  const hasCoords = /^-?\d{1,3}(?:\.\d+)?\s*,\s*-?\d{1,3}(?:\.\d+)?$/.test(coordsText);
  const coordQuery = hasCoords ? `loc:${coordsText}` : '';
  const displayQuery = addressText || labelText || coordsText;
  const embedBase = 'https://www.google.com/maps';

  if (hasCoords) {
    const ll = coordsText.split(',').map((part) => String(part).trim()).join(',');
    return {
      embedSrc: `${embedBase}?hl=es&ll=${encodeURIComponent(ll)}&q=${encodeURIComponent(coordQuery)}&z=${zoom}&output=embed`,
      dirSrc: `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(ll)}`,
      mapsSrc: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ll)}`
    };
  }

  return {
    embedSrc: `${embedBase}?hl=es&q=${encodeURIComponent(displayQuery)}&z=${zoom}&output=embed`,
    dirSrc: `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(displayQuery)}`,
    mapsSrc: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(displayQuery)}`
  };
}

export default function PedidoDeliveryNote({
  notas,
  latitud = null,
  longitud = null,
  direccion = null,
  metodoEntrega = null,
  sucursal = null,
  sucursalDireccion = null,
  sucursalLatitud = null,
  sucursalLongitud = null,
  sucursalLocalidad = null
}) {
  const info = useMemo(() => parseDelivery(notas), [notas]);
  const [copied, setCopied] = useState(false);
  const [mapTarget, setMapTarget] = useState('delivery');

  // Prioridad: columnas estructuradas del pedido > parseo del texto de notas.
  const metodo = String(metodoEntrega || '').trim().toLowerCase();
  const direccionCol = String(direccion || '').trim();
  const isMostrador = metodo === 'mostrador' || /retiro en mostrador/i.test(direccionCol);
  const address =
    direccionCol && !/retiro en mostrador/i.test(direccionCol)
      ? direccionCol
      : info.domicilio;
  const isDomicilio = !isMostrador && (metodo === 'domicilio' || !!address);

  const latNum = Number(latitud);
  const lngNum = Number(longitud);
  const hasExactCoords = Number.isFinite(latNum) && Number.isFinite(lngNum);
  const clientCoords = hasExactCoords ? `${latNum},${lngNum}` : info.coords;
  const branch = String(sucursal || '').trim() || info.sucursal;
  const branchAddressParts = [String(sucursalDireccion || '').trim(), String(sucursalLocalidad || '').trim()].filter(Boolean);
  const branchAddress = branchAddressParts.join(', ') || branch || '';
  const branchLatNum = Number(sucursalLatitud);
  const branchLngNum = Number(sucursalLongitud);
  const hasBranchCoords = Number.isFinite(branchLatNum) && Number.isFinite(branchLngNum);
  const branchCoords = hasBranchCoords ? `${branchLatNum},${branchLngNum}` : '';
  const hasBranch = Boolean(branch && (branchAddress || branchCoords));

  if (!isDomicilio || !address) return null;

  const showingBranchMap = hasBranch && mapTarget === 'branch';
  const activeStageLabel = showingBranchMap ? 'Preparación' : 'Entrega';
  const activeMapLabel = showingBranchMap ? `Sucursal: ${branch}` : 'Ubicación del cliente';
  const activeAddress = showingBranchMap ? branchAddress : address;
  const activeCoords = showingBranchMap ? branchCoords : clientCoords;

  const zoom = showingBranchMap ? (branchCoords ? 17 : 15) : (clientCoords ? 17 : 16);
  const { embedSrc, dirSrc, mapsSrc } = buildMapSources({
    coords: activeCoords,
    address: activeAddress,
    label: showingBranchMap ? branch : 'Cliente',
    zoom
  });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      /* noop */
    }
  };

  const handleToggleFocus = () => {
    if (!hasBranch) return;
    setMapTarget((current) => (current === 'delivery' ? 'branch' : 'delivery'));
  };

  return (
    <div className="pdn-card">
      <div className="pdn-card__inner">
        <div className="pdn-head">
          <div className="pdn-head__row">
            <span className="pdn-head__icon" aria-hidden="true">📍</span>
            <span className="pdn-head__kicker">Envío a domicilio</span>
            {hasBranch ? (
              <button
                type="button"
                className={`pdn-head__toggle${showingBranchMap ? ' is-branch' : ''}`}
                onClick={handleToggleFocus}
                aria-label={`Cambiar foco de ${activeStageLabel === 'Entrega' ? 'entrega' : 'preparación'} a ${activeStageLabel === 'Entrega' ? 'preparación' : 'entrega'}`}
                aria-pressed={showingBranchMap}
                title={`Mapa actual: ${activeStageLabel}`}
              >
                <span className="pdn-head__toggle-track">
                  <span className="pdn-head__toggle-knob" />
                </span>
              </button>
            ) : null}
            {info.pago ? <span className="pdn-chip">💳 {info.pago}</span> : null}
          </div>
          <strong className="pdn-head__title">{address}</strong>
        </div>

        <div className="pdn-flow" aria-label="Resumen operativo del pedido">
          <div className={`pdn-flow__strip${branch ? '' : ' is-single'}`}>
            <article className={`pdn-flow__segment pdn-flow__segment--delivery${!showingBranchMap ? ' is-active' : ''}`}>
              <span className="pdn-flow__eyebrow">Entrega</span>
              <strong className="pdn-flow__title">A domicilio</strong>
              <small className="pdn-flow__copy">Destino confirmado para reparto</small>
            </article>
            {branch ? (
              <article className={`pdn-flow__segment pdn-flow__segment--branch${showingBranchMap ? ' is-active' : ''}`}>
                <span className="pdn-flow__eyebrow">Preparaci&oacute;n</span>
                <strong className="pdn-flow__title">{branch}</strong>
                <small className="pdn-flow__copy">Sucursal asignada para armado y salida</small>
              </article>
            ) : null}
          </div>
        </div>

        <div className="pdn-mapwrap">
          <span className="pdn-pin">
            <span className="pdn-pin__dot" aria-hidden="true" />
            {activeMapLabel}
          </span>
          <iframe
            title={showingBranchMap ? 'Mapa de sucursal asignada' : 'Mapa de entrega a domicilio'}
            className="pdn-map"
            src={embedSrc}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
          <div className="pdn-map__glow" aria-hidden="true" />
        </div>

        <div className="pdn-actions">
          <a className="pdn-btn pdn-btn--primary" href={dirSrc} target="_blank" rel="noreferrer">
            🧭 Cómo llegar
          </a>
          <a className="pdn-btn" href={mapsSrc} target="_blank" rel="noreferrer">
            🗺️ Abrir en Maps
          </a>
          <button
            type="button"
            className={`pdn-btn${copied ? ' pdn-btn--ok' : ''}`}
            onClick={handleCopy}
          >
            {copied ? '✓ Copiado' : '📋 Copiar dirección'}
          </button>
        </div>
      </div>
    </div>
  );
}
